import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { Input, Select } from '../../../shared/components'
import { processCreditCardPayment } from '../services/paymentService'
import { PaymentMethod } from '../../../services'

const CSS_HANDLES = [
  'asaasPaymentForm',
  'paymentForm',
  'errorMessage',
  'submitButton',
  'loadingSpinner'
] as const

interface AsaasPaymentFormProps {
  onSuccess?: (paymentIntentId: string) => void
  onError?: (error: string) => void
  amount: number
  className?: string
  paymentMethod?: PaymentMethod.CREDIT_CARD | PaymentMethod.DEBIT_CARD
  clientSecret?: string
  orderId?: string
}

interface CardFormData {
  number: string
  holderName: string
  expiryMonth: string
  expiryYear: string
  cvv: string
  installments: number
}

const formatCardNumber = (value: string) => {
  const v = value.replace(/\D/g, '')
  const parts = v.match(/.{1,4}/g)
  return parts ? parts.join(' ') : v
}

const validateLuhn = (number: string) => {
  const digits = number.replace(/\D/g, '')
  if (digits.length < 13 || digits.length > 19) return false
  
  let sum = 0
  let isEven = false
  
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits.charAt(i), 10)
    if (isEven) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    sum += digit
    isEven = !isEven
  }
  
  return sum % 10 === 0
}

const AsaasPaymentForm: React.FC<AsaasPaymentFormProps> = ({
  onSuccess,
  onError,
  amount,
  className = '',
  paymentMethod = PaymentMethod.CREDIT_CARD,
  clientSecret,
  orderId
}) => {
  const handles = useCssHandles(CSS_HANDLES)
  
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
    setError
  } = useForm<CardFormData>({
    mode: 'onSubmit',
    defaultValues: {
      number: '',
      holderName: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      installments: 1
    }
  })

  const installments = watch('installments')
  const maxInstallments = Math.min(12, Math.floor(amount / 10))
  const installmentValue = amount / installments

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const onSubmit = async (data: CardFormData) => {
    if (!clientSecret) {
      setError('root', { type: 'manual', message: 'Pagamento não inicializado. Aguarde...' })
      onError?.('Pagamento não inicializado. Aguarde...')
      return
    }

    if (!orderId) {
      setError('root', { type: 'manual', message: 'Pedido não encontrado. Por favor, recarregue a página.' })
      onError?.('Pedido não encontrado. Por favor, recarregue a página.')
      return
    }

    try {
      const payment = await processCreditCardPayment({
        orderId: orderId,
        amount,
        installments: data.installments,
        cardData: {
          number: data.number.replace(/\s/g, ''),
          holderName: data.holderName,
          expiryMonth: data.expiryMonth.padStart(2, '0'),
          expiryYear: data.expiryYear,
          cvv: data.cvv
        }
      })

      if (payment.status === 'PAID') {
        onSuccess?.(payment.transactionId)
      } else {
        throw new Error('Pagamento não foi aprovado')
      }
    } catch (err) {
      console.error('[AsaasPaymentForm] Erro ao processar pagamento:', err)
      const message = err instanceof Error ? err.message : 'Erro ao processar pagamento'
      
      // Mapear erros comuns para o formulário
      if (message.toLowerCase().includes('número do cartão') || message.toLowerCase().includes('número de cartão')) {
        setError('number', { type: 'manual', message: 'Número de cartão inválido ou recusado.' })
      } else if (message.toLowerCase().includes('expirado') || message.toLowerCase().includes('validade')) {
        setError('expiryMonth', { type: 'manual', message: 'Verifique a validade.' })
        setError('expiryYear', { type: 'manual', message: 'Verifique a validade.' })
      } else if (message.toLowerCase().includes('cvv') || message.toLowerCase().includes('segurança')) {
        setError('cvv', { type: 'manual', message: 'CVV inválido.' })
      }
      
      setError('root', { type: 'manual', message })
      onError?.(message)
    }
  }

  return (
    <form 
      onSubmit={handleSubmit(onSubmit)} 
      className={`${handles.asaasPaymentForm} ${className}`}
    >
      {/* Seletor de Parcelas - apenas para cartão de crédito */}
      {paymentMethod === PaymentMethod.CREDIT_CARD && amount >= 10 && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Parcelamento
          </label>
          <Controller
            name="installments"
            control={control}
            rules={{ required: 'Selecione o parcelamento' }}
            render={({ field }) => (
              <>
                <Select
                  value={field.value.toString()}
                  onChange={(value: string) => field.onChange(Number(value))}
                  options={Array.from({ length: maxInstallments }, (_, i) => i + 1).map((num) => ({
                    value: num.toString(),
                    label: `${num}x de ${formatPrice(amount / num)}${num === 1 ? ' (à vista)' : ''}`
                  }))}
                  placeholder="Selecione o parcelamento"
                  className="w-full"
                />
                {errors.installments && (
                  <p className="mt-1 text-sm text-red-600">{errors.installments.message}</p>
                )}
              </>
            )}
          />
          {installments > 1 && (
            <p className="mt-2 text-xs text-gray-600">
              Total: {formatPrice(amount)} em {installments}x de {formatPrice(installmentValue)}
            </p>
          )}
        </div>
      )}

      <div className={`${handles.paymentForm} space-y-4 mb-6`}>
        {/* Número do Cartão */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Número do Cartão
          </label>
          <Controller
            name="number"
            control={control}
            rules={{
              required: 'Número do cartão é obrigatório',
              validate: (value) => {
                if (!value) return 'Número do cartão é obrigatório'
                const clean = value.replace(/\D/g, '')
                if (clean.length < 13 || clean.length > 19) return 'Número deve ter entre 13 e 19 dígitos'
                if (!validateLuhn(clean)) return 'Número de cartão inválido (erro de dígito)'
                return true
              }
            }}
            render={({ field }) => (
              <>
                <Input
                  type="text"
                  value={field.value}
                  onChange={(e) => {
                    const formatted = formatCardNumber(e.target.value)
                    field.onChange(formatted)
                  }}
                  placeholder="0000 0000 0000 0000"
                  maxLength={19}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.number ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.number && (
                  <p className="mt-1 text-sm text-red-600">{errors.number.message}</p>
                )}
              </>
            )}
          />
        </div>

        {/* Nome do Portador */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nome no Cartão
          </label>
          <Controller
            name="holderName"
            control={control}
            rules={{
              required: 'Nome no cartão é obrigatório'
            }}
            render={({ field }) => (
              <>
                <Input
                  type="text"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value.toUpperCase())
                  }}
                  placeholder="NOME COMO ESTÁ NO CARTÃO"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.holderName ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.holderName && (
                  <p className="mt-1 text-sm text-red-600">{errors.holderName.message}</p>
                )}
              </>
            )}
          />
        </div>

        {/* Validade e CVV */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Validade
            </label>
            <div className="grid grid-cols-2 gap-2">
              <Controller
                name="expiryMonth"
                control={control}
                rules={{
                  required: 'Mês é obrigatório',
                  validate: (value) => {
                    const month = parseInt(value, 10)
                    if (isNaN(month) || month < 1 || month > 12) return 'Mês inválido'
                    return true
                  }
                }}
                render={({ field }) => (
                  <>
                    <Input
                      type="text"
                      value={field.value}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 2)
                        field.onChange(value)
                      }}
                      onBlur={(e) => {
                        const value = e.target.value
                        if (value && parseInt(value) >= 1 && parseInt(value) <= 12) {
                          field.onChange(value.padStart(2, '0'))
                        }
                        field.onBlur()
                      }}
                      placeholder="MM"
                      maxLength={2}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.expiryMonth ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.expiryMonth && (
                      <p className="mt-1 text-xs text-red-600">{errors.expiryMonth.message}</p>
                    )}
                  </>
                )}
              />
              <Controller
                name="expiryYear"
                control={control}
                rules={{
                  required: 'Ano é obrigatório',
                  validate: (value, formValues) => {
                    const year = parseInt(value, 10)
                    const currentYear = new Date().getFullYear()
                    if (isNaN(year) || year < currentYear) return 'Ano inválido'
                    if (year > currentYear + 20) return 'Ano muito distante'
                    
                    // Validar se o par mês/ano já passou
                    const month = parseInt(formValues.expiryMonth, 10)
                    if (!isNaN(month) && year === currentYear) {
                      const currentMonth = new Date().getMonth() + 1
                      if (month < currentMonth) return 'Cartão expirado'
                    }
                    
                    return true
                  }
                }}
                render={({ field }) => (
                  <>
                    <Input
                      type="text"
                      value={field.value}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 4)
                        field.onChange(value)
                      }}
                      placeholder="AAAA"
                      maxLength={4}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.expiryYear ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.expiryYear && (
                      <p className="mt-1 text-xs text-red-600">{errors.expiryYear.message}</p>
                    )}
                  </>
                )}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CVV
            </label>
            <Controller
              name="cvv"
              control={control}
              rules={{
                required: 'CVV é obrigatório',
                pattern: {
                  value: /^\d{3,4}$/,
                  message: 'CVV deve ter 3 ou 4 dígitos'
                }
              }}
              render={({ field }) => (
                <>
                  <Input
                    type="text"
                    value={field.value}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4)
                      field.onChange(value)
                    }}
                    placeholder="123"
                    maxLength={4}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.cvv ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.cvv && (
                    <p className="mt-1 text-sm text-red-600">{errors.cvv.message}</p>
                  )}
                </>
              )}
            />
          </div>
        </div>
      </div>

      {errors.root && (
        <div className={`${handles.errorMessage} mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg shadow-sm flex items-start space-x-3`}>
          <div className="flex-shrink-0 text-red-500">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-bold text-red-800">Pagamento Recusado</h4>
            <p className="text-sm text-red-700 mt-1">{errors.root.message}</p>
          </div>
        </div>
      )}

      <div className="text-sm text-gray-600 mb-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span>Total a pagar:</span>
            <span className="font-semibold text-lg text-gray-900">
              {formatPrice(amount)}
            </span>
          </div>
          {installments > 1 && (
            <div className="flex items-center justify-between text-xs bg-blue-50 p-2 rounded">
              <span className="text-blue-700">Valor por parcela:</span>
              <span className="font-semibold text-blue-900">
                {formatPrice(installmentValue)}
              </span>
            </div>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={!clientSecret || !orderId || isSubmitting}
        className={`${handles.submitButton} w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center`}
      >
        {isSubmitting ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processando Pagamento...
          </>
        ) : (
          'Confirmar Pagamento'
        )}
      </button>

      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>🔒 Pagamento seguro processado pela Asaas</p>
        <p className="mt-1">Seus dados de cartão são criptografados e seguros</p>
      </div>
    </form>
  )
}

export default AsaasPaymentForm
